import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Plus, Trash, MagnifyingGlass, Power, Key, PencilSimple, ShieldCheck,
  UsersThree, Scales, ClockCounterClockwise, Lock, CheckSquare,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

const DEPT_LABEL = { collections: 'إدارة التحصيل', legal: 'الإدارة القانونية', shared: 'مشتركة' };
const ACTION_LABEL = {
  create: 'إنشاء', update: 'تعديل', delete: 'حذف',
  reset_password: 'إعادة تعيين كلمة المرور', toggle_active: 'تفعيل/إيقاف',
};

// ============================================================
// Users list + create/edit + reset password + toggle active
// ============================================================
function UsersTab({ me, roles, catalog }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [permsUser, setPermsUser] = useState(null);
  const [permsOverride, setPermsOverride] = useState([]);
  const empty = { name: '', email: '', password: '', role: 'agent', branch: '', departments: [], permissions: [] };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get('/users').then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);

  const roleByCode = useMemo(() => Object.fromEntries(roles.map(r => [r.code, r])), [roles]);
  const roleLabel = (code) => roleByCode[code]?.name || code;

  const filtered = useMemo(() => {
    return items.filter(u => {
      if (statusFilter === 'active' && u.is_active === false) return false;
      if (statusFilter === 'inactive' && u.is_active !== false) return false;
      if (deptFilter !== 'all' && !(u.departments || []).includes(deptFilter)) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (u.name || '').toLowerCase().includes(s)
        || (u.email || '').toLowerCase().includes(s)
        || roleLabel(u.role).includes(q);
    });
  }, [items, q, statusFilter, deptFilter, roleByCode]); // eslint-disable-line

  const save = async () => {
    if (!form.name?.trim() || !form.email?.trim()) { toast.error('الاسم والبريد مطلوبان'); return; }
    if (!editUser && (!form.password || form.password.length < 6)) {
      toast.error('كلمة المرور 6 أحرف فأكثر'); return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        branch: form.branch || null,
        departments: form.departments,
      };
      if (form.password) payload.password = form.password;
      if (editUser) {
        await api.put(`/users/${editUser.id}`, payload);
        toast.success('تم تحديث المستخدم');
      } else {
        await api.post('/users', payload);
        toast.success('تم إنشاء المستخدم');
      }
      setOpen(false); setEditUser(null); setForm(empty);
      load();
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
  };

  const openCreate = () => {
    setEditUser(null);
    setForm({ ...empty, departments: ['collections'] });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      name: u.name || '', email: u.email || '', password: '',
      role: u.role || 'agent', branch: u.branch || '',
      departments: u.departments || [], permissions: u.permissions || [],
    });
    setOpen(true);
  };
  const openPerms = (u) => {
    setPermsUser(u);
    setPermsOverride(u.permissions || []);
  };

  const savePerms = async () => {
    try {
      await api.put(`/users/${permsUser.id}`, { permissions: permsOverride });
      toast.success('تم حفظ الصلاحيات المخصّصة');
      setPermsUser(null); load();
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
  };

  const del = async (u) => {
    if (!window.confirm(`حذف المستخدم "${u.name}"؟ هذا الإجراء لا يمكن التراجع عنه.`)) return;
    try { await api.delete(`/users/${u.id}`); load(); toast.success('تم الحذف'); }
    catch (err) { toast.error(err.response?.data?.detail || 'فشل الحذف'); }
  };
  const toggleActive = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/toggle-active`);
      toast.success(data.is_active ? 'تم تفعيل المستخدم' : 'تم إيقاف المستخدم');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل'); }
  };
  const resetPassword = async () => {
    if (newPw.length < 6) { toast.error('كلمة المرور 6 أحرف فأكثر'); return; }
    try {
      await api.post(`/users/${pwUser.id}/reset-password`, { password: newPw });
      toast.success('تم إعادة تعيين كلمة المرور');
      setPwUser(null); setNewPw('');
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل'); }
  };

  const fmtLast = (d) => d ? new Date(d).toLocaleString('ar-SA', { hour12: false }) : '—';
  const toggleDept = (code) => {
    const s = new Set(form.departments);
    if (s.has(code)) s.delete(code); else s.add(code);
    setForm({ ...form, departments: Array.from(s) });
  };
  const togglePerm = (code) => {
    const s = new Set(permsOverride);
    if (s.has(code)) s.delete(code); else s.add(code);
    setPermsOverride(Array.from(s));
  };
  const inheritedPerms = permsUser ? new Set(roleByCode[permsUser.role]?.permissions || []) : new Set();
  const permCount = permsOverride.length;

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex-1 flex items-end gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم، البريد، أو الدور" className="pr-10 h-9" data-testid="users-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الحالات</SelectItem>
              <SelectItem value="active">نشط</SelectItem>
              <SelectItem value="inactive">غير نشط</SelectItem>
            </SelectContent>
          </Select>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="users-dept-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الإدارات</SelectItem>
              <SelectItem value="collections">إدارة التحصيل</SelectItem>
              <SelectItem value="legal">الإدارة القانونية</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openCreate} className="bg-slate-900 text-white" data-testid="new-user-btn"><Plus className="ms-1" size={16} /> مستخدم جديد</Button>
      </div>

      <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="text-start p-3">المستخدم</th>
              <th className="text-start p-3">الدور</th>
              <th className="text-start p-3">الإدارات</th>
              <th className="text-start p-3">الفرع</th>
              <th className="text-start p-3">الحالة</th>
              <th className="text-start p-3">آخر دخول</th>
              <th className="text-start p-3">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">لا توجد نتائج</td></tr>
            ) : filtered.map(u => {
              const active = u.is_active !== false;
              const overrideCount = (u.permissions || []).length;
              return (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">{(u.name || '?').charAt(0)}</div>
                      <div>
                        <div className="font-medium text-slate-900">{u.name}</div>
                        <div className="text-xs text-slate-500 mono">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline">{roleLabel(u.role)}</Badge>
                    {overrideCount > 0 && (
                      <div className="text-[10px] text-amber-700 mt-1">+ {overrideCount} صلاحية مخصّصة</div>
                    )}
                  </td>
                  <td className="p-3 text-slate-600">
                    <div className="flex gap-1 flex-wrap">
                      {(u.departments || []).map(d => (
                        <span key={d} className="text-[11px] bg-slate-100 px-2 py-0.5 rounded">{DEPT_LABEL[d] || d}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-slate-600">{u.branch || '—'}</td>
                  <td className="p-3">
                    {active ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> نشط</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> غير نشط</span>
                    )}
                  </td>
                  <td className="p-3 text-xs mono text-slate-500">{fmtLast(u.last_login)}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" title="تعديل" onClick={() => openEdit(u)} data-testid={`edit-${u.email}`}><PencilSimple size={16} /></Button>
                      <Button size="icon" variant="ghost" title="صلاحيات دقيقة" onClick={() => openPerms(u)} data-testid={`perms-${u.email}`}><ShieldCheck size={16} /></Button>
                      <Button size="icon" variant="ghost" title="إعادة تعيين كلمة المرور" onClick={() => { setPwUser(u); setNewPw(''); }} data-testid={`resetpw-${u.email}`}><Key size={16} /></Button>
                      <Button size="icon" variant="ghost" title={active ? 'إيقاف' : 'تفعيل'} onClick={() => toggleActive(u)} disabled={u.id === me?.id} data-testid={`toggle-${u.email}`}>
                        <Power size={16} className={active ? 'text-red-600' : 'text-emerald-600'} />
                      </Button>
                      <Button size="icon" variant="ghost" title="حذف" onClick={() => del(u)} disabled={u.id === me?.id} data-testid={`del-${u.email}`}><Trash size={16} className="text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Create/Edit user dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditUser(null); setForm(empty); } }}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader><DialogTitle>{editUser ? 'تعديل المستخدم' : 'مستخدم جديد'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm mb-1 block">الاسم *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" /></div>
              <div><Label className="text-sm mb-1 block">البريد الإلكتروني *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" /></div>
            </div>
            <div>
              <Label className="text-sm mb-1 block">كلمة المرور {editUser && <span className="text-xs text-slate-400">(اترك فارغاً للإبقاء)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="form-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm mb-1 block">الدور *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {roleByCode[form.role]?.description && (
                  <div className="text-[11px] text-slate-500 mt-1">{roleByCode[form.role].description}</div>
                )}
              </div>
              <div><Label className="text-sm mb-1 block">الفرع</Label><Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="اختياري" data-testid="form-branch" /></div>
            </div>
            <div>
              <Label className="text-sm mb-2 block">الإدارات (يمكن تعيين أكثر من إدارة)</Label>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.departments.includes('collections')} onCheckedChange={() => toggleDept('collections')} data-testid="dept-collections" />
                  إدارة التحصيل
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.departments.includes('legal')} onCheckedChange={() => toggleDept('legal')} data-testid="dept-legal" />
                  الإدارة القانونية
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} className="bg-slate-900 text-white" data-testid="save-user-btn">{editUser ? 'حفظ التعديلات' : 'إنشاء'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!pwUser} onOpenChange={(v) => { if (!v) { setPwUser(null); setNewPw(''); } }}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader><DialogTitle>إعادة تعيين كلمة المرور</DialogTitle></DialogHeader>
          {pwUser && <div className="text-sm text-slate-600 mb-3">للمستخدم: <span className="font-semibold text-slate-900">{pwUser.name}</span> ({pwUser.email})</div>}
          <Label className="text-sm mb-1 block">كلمة المرور الجديدة (6+ أحرف)</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-pw-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwUser(null); setNewPw(''); }}>إلغاء</Button>
            <Button onClick={resetPassword} className="bg-slate-900 text-white" data-testid="reset-pw-submit"><Key className="ms-1" size={14} /> إعادة التعيين</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-user granular permissions override */}
      <Dialog open={!!permsUser} onOpenChange={(v) => { if (!v) setPermsUser(null); }}>
        <DialogContent dir="rtl" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>صلاحيات دقيقة — {permsUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 mb-3">
            الصلاحيات الموروثة من الدور <strong>{roleLabel(permsUser?.role)}</strong> مفعّلة تلقائياً.
            هنا يمكنك إضافة صلاحيات <em>إضافية</em> فوق الدور. عدد الإضافات: <strong>{permCount}</strong>
          </div>
          <div className="space-y-4">
            {catalog.map(group => (
              <div key={group.group} className="border border-slate-200 rounded-md">
                <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 border-b">{group.label}</div>
                <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {group.codes.map(c => {
                    const inherited = inheritedPerms.has(c.code);
                    const custom = permsOverride.includes(c.code);
                    return (
                      <label key={c.code} className={`flex items-center gap-2 text-sm p-1.5 rounded cursor-pointer hover:bg-slate-50 ${inherited ? 'opacity-70' : ''}`} title={c.code}>
                        <Checkbox
                          checked={inherited || custom}
                          disabled={inherited}
                          onCheckedChange={() => togglePerm(c.code)}
                          data-testid={`perm-${c.code}`}
                        />
                        <span className="flex-1">{c.label}</span>
                        {inherited ? <span className="text-[10px] text-emerald-600">موروثة</span> :
                          custom ? <span className="text-[10px] text-amber-600">مضافة</span> : null}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermsUser(null)}>إلغاء</Button>
            <Button onClick={savePerms} className="bg-slate-900 text-white" data-testid="save-perms-btn"><CheckSquare className="ms-1" size={14} /> حفظ الصلاحيات</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Roles CRUD
// ============================================================
function RolesTab({ catalog, reloadRoles, roles }) {
  const [open, setOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const empty = { code: '', name: '', description: '', department: 'collections', permissions: [] };
  const [form, setForm] = useState(empty);

  const openCreate = () => { setEditRole(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => {
    setEditRole(r);
    setForm({
      code: r.code, name: r.name, description: r.description || '',
      department: r.department, permissions: r.permissions || [],
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name?.trim()) { toast.error('اسم الدور مطلوب'); return; }
    try {
      if (editRole) {
        await api.put(`/roles/${editRole.code}`, {
          name: form.name.trim(),
          description: form.description,
          department: form.department,
          permissions: form.permissions,
        });
        toast.success('تم تحديث الدور');
      } else {
        if (!form.code?.trim()) { toast.error('كود الدور مطلوب'); return; }
        await api.post('/roles', {
          code: form.code.trim(),
          name: form.name.trim(),
          description: form.description,
          department: form.department,
          permissions: form.permissions,
        });
        toast.success('تم إنشاء الدور');
      }
      setOpen(false); setEditRole(null); reloadRoles();
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
  };
  const del = async (r) => {
    if (!window.confirm(`حذف الدور "${r.name}"؟`)) return;
    try { await api.delete(`/roles/${r.code}`); toast.success('تم الحذف'); reloadRoles(); }
    catch (err) { toast.error(err.response?.data?.detail || 'فشل'); }
  };
  const togglePerm = (code) => {
    const s = new Set(form.permissions);
    if (s.has(code)) s.delete(code); else s.add(code);
    setForm({ ...form, permissions: Array.from(s) });
  };
  const toggleGroup = (group, allSelected) => {
    const s = new Set(form.permissions);
    group.codes.forEach(c => { if (allSelected) s.delete(c.code); else s.add(c.code); });
    setForm({ ...form, permissions: Array.from(s) });
  };

  return (
    <div className="space-y-4" data-testid="roles-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          الأدوار الافتراضية لا يمكن حذفها لكن يمكن تعديل صلاحياتها (باستثناء مدير النظام).
          أنشئ أدواراً مخصّصة لتعريف صلاحيات مجموعات مختلفة داخل النظام.
        </div>
        <Button onClick={openCreate} className="bg-slate-900 text-white" data-testid="new-role-btn"><Plus className="ms-1" size={16} /> دور جديد</Button>
      </div>

      <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="text-start p-3">الكود</th>
              <th className="text-start p-3">الاسم</th>
              <th className="text-start p-3">الإدارة</th>
              <th className="text-start p-3">الصلاحيات</th>
              <th className="text-start p-3">النوع</th>
              <th className="text-start p-3">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {roles.map(r => (
              <tr key={r.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`role-row-${r.code}`}>
                <td className="p-3 mono text-xs text-slate-600">{r.code}</td>
                <td className="p-3 font-medium">{r.name}<div className="text-[11px] text-slate-500 font-normal">{r.description}</div></td>
                <td className="p-3"><Badge variant="outline">{DEPT_LABEL[r.department] || r.department}</Badge></td>
                <td className="p-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{(r.permissions || []).length}</span></td>
                <td className="p-3">{r.is_system ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">افتراضي</Badge> : <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">مخصّص</Badge>}</td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" title="تعديل" onClick={() => openEdit(r)} disabled={r.code === 'admin'} data-testid={`edit-role-${r.code}`}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" title="حذف" onClick={() => del(r)} disabled={r.is_system} data-testid={`del-role-${r.code}`}><Trash size={16} className={r.is_system ? '' : 'text-red-600'} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editRole ? `تعديل الدور — ${editRole.name}` : 'دور جديد'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm mb-1 block">الكود (بالإنجليزية بدون فراغ) *</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!editRole} placeholder="collection_lead" data-testid="role-code" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">الاسم بالعربية *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="مثال: قائد فريق التحصيل" data-testid="role-name" />
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1 block">الوصف</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="role-desc" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">الإدارة</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })} disabled={editRole?.is_system}>
                <SelectTrigger data-testid="role-dept"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="collections">إدارة التحصيل</SelectItem>
                  <SelectItem value="legal">الإدارة القانونية</SelectItem>
                  <SelectItem value="shared">مشتركة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">الصلاحيات ({form.permissions.length})</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setForm({ ...form, permissions: catalog.flatMap(g => g.codes.map(c => c.code)) })} data-testid="select-all-perms">تحديد الكل</Button>
                  <Button variant="outline" size="sm" onClick={() => setForm({ ...form, permissions: [] })} data-testid="clear-all-perms">مسح الكل</Button>
                </div>
              </div>
              <div className="space-y-3">
                {catalog.map(group => {
                  const groupCodes = group.codes.map(c => c.code);
                  const allSelected = groupCodes.every(c => form.permissions.includes(c));
                  const someSelected = groupCodes.some(c => form.permissions.includes(c));
                  return (
                    <div key={group.group} className="border border-slate-200 rounded-md">
                      <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b">
                        <div className="text-xs font-semibold text-slate-700">{group.label}</div>
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <Checkbox checked={allSelected} onCheckedChange={() => toggleGroup(group, allSelected)} data-testid={`group-${group.group}`} />
                          {allSelected ? 'الكل' : someSelected ? 'جزئي' : 'الكل'}
                        </label>
                      </div>
                      <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {group.codes.map(c => (
                          <label key={c.code} className="flex items-center gap-2 text-sm p-1.5 rounded cursor-pointer hover:bg-slate-50" title={c.code}>
                            <Checkbox checked={form.permissions.includes(c.code)} onCheckedChange={() => togglePerm(c.code)} data-testid={`role-perm-${c.code}`} />
                            <span className="flex-1">{c.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} className="bg-slate-900 text-white" data-testid="save-role-btn"><CheckSquare className="ms-1" size={14} /> {editRole ? 'حفظ التعديلات' : 'إنشاء الدور'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Audit Log tab (user/role events)
// ============================================================
function UserAuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/audit-log', { params: { entity: 'user,role', limit: 500 } });
      setLogs(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q) return logs;
    const s = q.toLowerCase();
    return logs.filter(l =>
      (l.user_email || '').toLowerCase().includes(s) ||
      (l.action || '').toLowerCase().includes(s) ||
      (l.entity || '').toLowerCase().includes(s) ||
      JSON.stringify(l.new_value || {}).toLowerCase().includes(s)
    );
  }, [logs, q]);

  const fmt = (d) => d ? new Date(d).toLocaleString('ar-SA', { hour12: false }) : '—';

  return (
    <div className="space-y-4" data-testid="user-audit-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث..." className="pr-10 h-9" data-testid="audit-search" />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>تحديث</Button>
        <div className="text-xs text-slate-500 ms-auto">إجمالي: {filtered.length}</div>
      </div>

      <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="text-start p-3">التاريخ</th>
              <th className="text-start p-3">المنفّذ</th>
              <th className="text-start p-3">الإجراء</th>
              <th className="text-start p-3">الكيان</th>
              <th className="text-start p-3">التفاصيل</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">{loading ? 'جاري التحميل...' : 'لا توجد سجلات'}</td></tr>
            ) : filtered.map(l => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 text-xs mono text-slate-500 whitespace-nowrap">{fmt(l.timestamp)}</td>
                <td className="p-3">
                  <div className="font-medium">{l.user_name}</div>
                  <div className="text-xs text-slate-500 mono">{l.user_email}</div>
                </td>
                <td className="p-3"><Badge variant="outline">{ACTION_LABEL[l.action] || l.action}</Badge></td>
                <td className="p-3"><Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{l.entity}</Badge></td>
                <td className="p-3 text-xs text-slate-600 max-w-md truncate" title={JSON.stringify(l.new_value || l.old_value || {})}>
                  {(() => {
                    // Prefer new_value; fall back to old_value for delete actions
                    const v = (l.new_value && Object.keys(l.new_value).length) ? l.new_value : (l.old_value || {});
                    if (v.email) return `${v.email}${v.role ? ` — ${v.role}` : ''}`;
                    if (v.code) return `${v.code} — ${v.name || ''}`;
                    if (Object.keys(v).length) return Object.keys(v).join(', ');
                    return '—';
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ============================================================
// Main page — with tabs
// ============================================================
export default function Users() {
  const { user: me } = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState('users');

  const reloadRoles = useCallback(() => api.get('/roles').then(({ data }) => setRoles(data)), []);
  useEffect(() => {
    reloadRoles();
    api.get('/permissions/catalog').then(({ data }) => setCatalog(data));
  }, [reloadRoles]);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Users, Roles & Permissions</div>
        <h1 className="text-3xl font-bold text-slate-900">المستخدمون والصلاحيات</h1>
        <p className="text-sm text-slate-500 mt-1">إدارة الحسابات والأدوار والصلاحيات الدقيقة عبر إدارة التحصيل والإدارة القانونية</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="users" data-testid="tab-users"><UsersThree size={16} className="ms-1" /> المستخدمون</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles"><Scales size={16} className="ms-1" /> الأدوار والصلاحيات</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ClockCounterClockwise size={16} className="ms-1" /> سجل التدقيق</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4">
          <UsersTab me={me} roles={roles} catalog={catalog} />
        </TabsContent>
        <TabsContent value="roles" className="mt-4">
          <RolesTab catalog={catalog} roles={roles} reloadRoles={reloadRoles} />
        </TabsContent>
        <TabsContent value="audit" className="mt-4">
          <UserAuditTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
